import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { metaTrack } from "@/lib/metaPixel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const PaymentSuccess = () => {
  const { t, lang } = useI18n();
  const { clearCart } = useCart();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const [resendEmail, setResendEmail] = useState("");
  const attempts = useRef(0);
  const cleared = useRef(false);
  const reported = useRef(false);
  const metaReported = useRef(false);

  const resendGuestLink = async () => {
    const em = resendEmail.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) {
      toast.error(lang === "en" ? "Enter a valid email" : "Inserisci un'email valida"); return;
    }
    try { await api.post("/guest/orders/resend", { email: em }); } catch {}
    toast.success(lang === "en"
      ? "If a paid order exists for this email, we've sent a new link."
      : "Se esiste un ordine pagato per questa email, ti abbiamo inviato un nuovo link.");
  };

  const fireAdsConversion = async (sessionId) => {
    if (reported.current) return;
    reported.current = true;
    try {
      const { data } = await api.post(`/payments/${sessionId}/ads-conversion`);
      if (data.report && typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18453018056/d9KECJuk3fgcEMjrit9E",
          value: data.value,
          currency: data.currency,
          transaction_id: data.transaction_id,
        });
      }
    } catch {}
  };

  // Meta Pixel Purchase: solo a pagamento confermato dal backend, deduplicato (event_id ordine)
  const fireMetaPurchase = async (sessionId) => {
    if (metaReported.current) return;
    metaReported.current = true;
    try {
      const { data } = await api.post(`/payments/${sessionId}/meta-purchase`);
      if (data.report) {
        metaTrack("Purchase", {
          value: data.value, currency: data.currency, content_type: "product",
          content_ids: data.content_ids, contents: data.contents,
          content_name: data.content_name, num_items: data.num_items,
        }, { eventID: data.event_id });
      }
    } catch {}
  };

  // Tracciamento OSPITE: stessa logica, endpoint guest (no auth), fonte di verita' server, dedup per-ordine
  const fireGuestAds = async (sessionId) => {
    if (reported.current) return;
    reported.current = true;
    try {
      const { data } = await api.post(`/guest/payments/${sessionId}/ads-conversion`);
      if (data.report && typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18453018056/d9KECJuk3fgcEMjrit9E",
          value: data.value, currency: data.currency, transaction_id: data.transaction_id,
        });
      }
    } catch {}
  };
  const fireGuestMeta = async (sessionId) => {
    if (metaReported.current) return;
    metaReported.current = true;
    try {
      const { data } = await api.post(`/guest/payments/${sessionId}/meta-purchase`);
      if (data.report) {
        metaTrack("Purchase", {
          value: data.value, currency: data.currency, content_type: "product",
          content_ids: data.content_ids, contents: data.contents,
          content_name: data.content_name, num_items: data.num_items,
        }, { eventID: data.event_id });
      }
    } catch {}
  };

  useEffect(() => {
    if (authLoading) return;
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        if (user) {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            setStatus("paid"); setOrder(data);
            if (!cleared.current) { clearCart(); cleared.current = true; }
            fireAdsConversion(sessionId);
            fireMetaPurchase(sessionId);
            return;
          }
        } else {
          // Ospite: stato COARSE (nessun dato personale via session_id). Consegna via email + link sicuro.
          const { data } = await api.get(`/guest/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            setStatus("paid-guest");
            if (!cleared.current) { clearCart(); cleared.current = true; }
            fireGuestAds(sessionId);
            fireGuestMeta(sessionId);
            return;
          }
        }
      } catch {}
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, clearCart, user, authLoading]);

  const consent = order?.consent;

  return (
    <div data-testid="payment-success-page" className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-brand-light">
      <div className="w-full max-w-md rounded-2xl border border-brand-navy/10 bg-white p-10 text-center">
        {status === "checking" ? (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-brand-electric animate-spin" />
            <p className="mt-5 text-brand-navy/60">{t("pay.verifying")}</p>
          </>
        ) : status === "paid" ? (
          <>
            <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center text-green-600"><CheckCircle2 className="h-9 w-9" /></div>
            <h1 className="mt-5 text-2xl font-bold font-heading text-brand-navy">{t("pay.successTitle")}</h1>
            <p className="mt-2 text-brand-navy/60">{t("pay.successSub")}</p>
            {consent && (
              <div data-testid="success-consent-summary" className="mt-5 text-left rounded-xl border border-brand-navy/10 bg-brand-light/50 p-4 text-xs text-brand-navy/70 space-y-1">
                <p className="font-semibold text-brand-navy/80 uppercase tracking-wider text-[10px]">{t("pay.consentTitle")}</p>
                <p>{t("pay.consentTerms")}</p>
                <p>{consent.supply_type === "smart_service" ? t("consent.immediateService") : t("consent.immediateDownload")}</p>
                <p className="text-brand-navy/50">{t("pay.consentVersion")}: {consent.legal_version} · {new Date(consent.accepted_at).toLocaleString()}</p>
                {order?.unlocked_at && <p className="text-brand-navy/50">{t("pay.consentUnlocked")}: {new Date(order.unlocked_at).toLocaleString()}</p>}
              </div>
            )}
            <Button data-testid="success-go-account" onClick={() => navigate("/account")} className="mt-6 rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white">{t("pay.goAccount")}</Button>
          </>
        ) : status === "paid-guest" ? (
          <>
            <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center text-green-600"><CheckCircle2 className="h-9 w-9" /></div>
            <h1 data-testid="guest-success-title" className="mt-5 text-2xl font-bold font-heading text-brand-navy">{t("pay.successTitle")}</h1>
            <p className="mt-2 text-brand-navy/70">
              {lang === "en"
                ? "Payment confirmed. We've emailed you a secure download link for your digital product."
                : "Pagamento confermato. Ti abbiamo inviato via email un link sicuro per scaricare il tuo prodotto digitale."}
            </p>
            <div data-testid="guest-resend-block" className="mt-6 text-left rounded-xl border border-brand-navy/10 bg-brand-light/50 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-brand-navy"><Mail className="h-4 w-4 text-brand-electric" /> {lang === "en" ? "Didn't get the email?" : "Non hai ricevuto l'email?"}</p>
              <p className="mt-1 text-xs text-brand-navy/60">{lang === "en" ? "Enter your email to receive a new secure link." : "Inserisci la tua email per ricevere un nuovo link sicuro."}</p>
              <div className="mt-2 flex gap-2">
                <Input data-testid="guest-resend-email" type="email" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} placeholder={lang === "en" ? "you@email.com" : "tua@email.com"} className="h-10" />
                <Button data-testid="guest-resend-btn" onClick={resendGuestLink} className="h-10 rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white whitespace-nowrap">{lang === "en" ? "Resend link" : "Reinvia link"}</Button>
              </div>
            </div>
            <Button data-testid="guest-success-home" onClick={() => navigate("/")} className="mt-6 rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white">{lang === "en" ? "Back to home" : "Torna alla home"}</Button>
          </>
        ) : (
          <>
            <p className="text-brand-navy/60">{t("pay.verifying")}</p>
            <Button data-testid="success-go-account" onClick={() => navigate("/account")} className="mt-6 rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white">{t("pay.goAccount")}</Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
